import { useEffect, useState } from 'react'

import { Box, Stack } from '@mui/material'

import { Card, Skeleton } from '@repo/ui'

import { SegmentedTabs } from 'components/navigation/SegmentedTabs/SegmentedTabs'
import { StickyFormHeader } from 'components/navigation/StickyFormHeader/StickyFormHeader'
import { useCanAccess } from 'hooks'
import { useUnifiedFieldMappingConfig } from 'hooks/api'
import { useSettingsStickyOffset } from 'pages/settings/SettingsPage/settingsStickyOffset'

import { FieldMappingEditor } from './components/FieldMappingEditor'
import { ScanDefaultsCard } from './components/ScanDefaultsCard'

type FieldsTab = 'mapping' | 'defaults'

const TABS: { label: string; value: FieldsTab }[] = [
  { label: 'Mapping', value: 'mapping' },
  { label: 'Defaults', value: 'defaults' },
]

/**
 * Settings → Fields (was "Field mapping", renamed here).
 *
 * Two halves of one subject — what fills a product's fields:
 *
 * - **Mapping** — which of a connected integration's fields populate each Vera field.
 *   The standalone view is the UNIFIED cross-integration aggregate (VRG-100): one table
 *   across every connected store, each source field badged + grouped by its store. The
 *   per-integration tab (Integrations → <provider> → Field mapping) stays scoped to that
 *   one provider; this page is the aggregate.
 * - **Defaults** — the company-wide values a product inherits when it sets none of its
 *   own. Today that is the scan instructions + media (VRG-323), moved here from Settings
 *   → Installation, which answered a different question ("where does a scan LAND") and
 *   only shared the word "scan".
 *
 * ## Why the rename, and why the two halves belong together
 *
 * Mapping REQUIRES a connected integration. Without one the whole section rendered "No
 * connected store" and nothing else — a permanently dead nav row for every company that
 * has not integrated. Defaults requires no integration. Folding them together turns a
 * dead row into one that is always useful, with Mapping as the conditional half rather
 * than the whole point.
 *
 * "Fields" is also the better name for what comes next: `products.custom_fields` already
 * exists in the schema, so custom field DEFINITIONS have a home here that a name
 * promising only "mapping" could not offer.
 */
export const FieldsPage = () => {
  /*
    Each half has its OWN view permission, and a half you cannot view is not a tab.

    §27 says disable-with-a-tooltip for actions and hide for surfaces; a tab is a
    surface, and there is a second reason here that decides it: the section is reachable
    on EITHER permission, so a user holding only one of them would otherwise be offered a
    toggle whose other position renders a card they are not allowed to see. A disabled
    toggle segment cannot explain itself the way a nav row can — it has no room for the
    sentence naming the permission — so the honest rendering is to show only the half
    that exists for this user. The left-nav row is where the "you need X" message lives
    (SettingsPage), and it is already correct for the both-missing case.
  */
  const canViewMapping = useCanAccess({ action: 'field_mapping_view' }).isHasAccess
  const canViewDefaults = useCanAccess({ action: 'field_default_view' }).isHasAccess

  /*
    How far below the scrollport top the title + toggle pin. NOT 0 — this page renders
    inside the settings shell, whose own "Settings" header is already sticky there, and the
    shell header's height CHANGES with its back-link row. `SettingsPage` publishes the number
    it already computes; reading it is what keeps this from being a second, drifting copy.
    See `settingsStickyOffset.ts` and the same call in `SupportPage`.
  */
  const stickyTop = useSettingsStickyOffset()

  /*
    Which half lands FIRST is the point of the change, not a detail.

    Renaming alone would still drop an un-integrated company on the dead half. So the
    landing tab follows the thing that decides whether Mapping can do anything at all:
    an integration connected → Mapping; none → Defaults.

    Same query key the editor uses (`['field-mapping','unified','config']`), so react-query
    dedupes it — this is not a second request.
  */
  // Not fetched at all when Mapping is out of reach: the endpoint is gated
  // `field_mapping_view`, so the request would 403 and the only thing its answer
  // decides — which half to land on — is already settled by not having the half.
  const config = useUnifiedFieldMappingConfig(canViewMapping)
  // Settled, not successful: `retry: false` means an error resolves the question too,
  // and an errored unified config is exactly the no-store case the editor already treats
  // as "nothing to map here".
  const isResolved = !config.isLoading
  const hasIntegration = !config.isError && (config.data?.integrations.length ?? 0) > 0

  /*
    `null` until resolved, rather than defaulting to 'mapping' and correcting.

    A default-then-correct would mount the mapping editor and swap it out a beat later for
    every company without an integration — a visible pop on the exact path this change
    exists to fix. Holding null costs one skeleton and makes the first paint the right one.
  */
  const [tab, setTab] = useState<FieldsTab | null>(null)

  useEffect(() => {
    // Only ever seeds the INITIAL value. Once the user has picked a half, a later refetch
    // resolving differently must not move them off the tab they are reading.
    if (tab !== null || !isResolved) return
    // Permission decides before the integration does. A user who cannot view Mapping has
    // exactly one landing tab regardless of how many stores are connected.
    if (!canViewMapping) return setTab('defaults')
    if (!canViewDefaults) return setTab('mapping')
    setTab(hasIntegration ? 'mapping' : 'defaults')
  }, [tab, isResolved, hasIntegration, canViewMapping, canViewDefaults])

  const visibleTabs = TABS.filter(({ value }) =>
    value === 'mapping' ? canViewMapping : canViewDefaults,
  )

  if (tab === null) return <FieldsPageSkeleton />

  return (
    <>
      {/*
        STICKY — the title AND the Mapping/Defaults toggle stay pinned.

        The toggle is the reason. It is the only way between the two halves of this page, and
        the Defaults half scrolls well past a viewport once its fields are filled in: a switch
        you have to scroll back up to reach is worse than one that never moved, because the
        moment you want the other half is the moment you have already scrolled away from the
        control that gets you there. So the toggle rides INSIDE the sticky block (as
        `children`) rather than sitting in the flow beneath it — the arrangement SupportPage
        and the long create/edit forms use, through the same shared component, so the fiddly
        parts (finding the real scroll parent, masking the scrollport's 24px top padding, the
        stuck-only hairline) stay solved once (§36).

        The title comes with it because it is what the block pins UNDER — leaving it in
        SettingsPage's flow would scroll a bare toggle bar over the page with nothing naming
        it. That is why the menu entry is `selfTitled`.

        `nested`, because this Header sits under the shell's own "Settings" header — it also
        sets the title→controls gap to a nested header's 12px gutter.
      */}
      <StickyFormHeader
        title="Fields"
        nested
        top={stickyTop}
        // 20px below the toggle, matching the `spacing={5}` this page used between the toggle
        // and the card beneath it, so moving the toggle into the sticky block changes where it
        // stays, not where it sits.
        bottomGap={5}
      >
        {/* `pt: 3` on top of the block's own 12px, restoring the 24px this page had between
            the title and the toggle (Header's `pb: 3` plus the Stack's `pt: 3`). Without it
            the rename to a sticky header would silently tighten the page by 12px. */}
        {/* No toggle at all when only one half is viewable — a one-position toggle is a
            control that cannot be operated, and the other position would lead to a card
            this user is not permitted to see. The page then reads as a plain titled
            section, which is what it is for them. */}
        {visibleTabs.length > 1 && (
          <Box sx={{ pt: 3 }}>
            {/* `SegmentedTabs`, the platform's tab control — the same one Billing and
                Roles & Permissions render. Measured before this change: 16 render sites
                on the platform against `@repo/ui` `ToggleButton`'s 6, and of those 6 this
                was the only TAB ROW; the rest are form/preview controls that happen to
                look segmented. So this page was the outlier, not a fifth pattern.

                The two are one design and now measure identically on track height (48),
                padding (4px) and pill (40px), but they are NOT interchangeable — the
                selected-value prop is `value` here and `tab` there, and `SegmentedTab`
                carries no `icon`, which is what keeps the two Sun/Moon toggles on
                `ToggleButton`. */}
            <SegmentedTabs
              tabs={visibleTabs}
              value={tab}
              onChange={(value) => setTab(value as FieldsTab)}
            />
          </Box>
        )}
      </StickyFormHeader>

      <Stack spacing={5} sx={{ flex: 1 }}>
      {/*
        Unmounted, not hidden. Each half owns a dirty-checked form, and keeping the
        inactive one mounted would let a user leave unsaved edits behind a tab with no
        indication they exist.

        `provider="shopify"` is the editor's fallback for rows whose connection cannot be
        resolved; `unified` is what makes it the cross-integration aggregate.
      */}
      {tab === 'mapping' ? (
        <FieldMappingEditor provider="shopify" unified fullPage />
      ) : (
        <ScanDefaultsCard />
      )}
      </Stack>
    </>
  )
}

/**
 * §19 footprint parity for the one frame before the landing tab is known: the toggle at
 * its real 48px track height and a card-shaped body, in the same `spacing={5}` rhythm as
 * the loaded page, so the toggle does not jump when the content arrives.
 *
 * The body is deliberately generic — which half is coming is precisely what is not known
 * yet, and the two have different heights. Each half then renders its OWN skeleton while
 * its data loads (`FieldMappingEditorSkeleton` / `ScanDefaultsCardSkeleton`), which is
 * where the real 1:1 parity lives.
 */
const FieldsPageSkeleton = () => {
  const stickyTop = useSettingsStickyOffset()

  return (
  <>
    {/* The REAL header, not a skeleton of one. The page is `selfTitled`, so this component
        owns the title in the loading frame too — a bar where the title belongs would be a
        stand-in for text that is already known. Same block, same offsets, so the toggle's
        skeleton sits exactly where the toggle lands. */}
    <StickyFormHeader title="Fields" nested top={stickyTop} bottomGap={5}>
      <Box sx={{ pt: 3 }}>
        {/* 48 and 10px are `SegmentedTabs`' track, verbatim — a 40px pill inside 4px of
            track padding, at the track's own radius. */}
        <Skeleton sx={{ width: 200, height: 48, borderRadius: '10px' }} />
      </Box>
    </StickyFormHeader>

    <Stack spacing={5} sx={{ flex: 1 }}>
    <Card>
      <Stack spacing={5}>
        <Stack spacing={1}>
          <Skeleton sx={{ width: 220, height: 24 }} />
          <Skeleton lines={2} sx={{ maxWidth: 520 }} />
        </Stack>
        <Skeleton sx={{ width: '100%', height: 160, borderRadius: 2 }} />
      </Stack>
    </Card>
    </Stack>
  </>
  )
}
